// C-1: the browse screen container — redux wiring for BrowseSections.
//
// Reuses the existing broadcastList redux flow (listBroadcasts → /api/
// broadcasts) and normalizes the payload through the A-2 normalizer at the
// edge; nothing below this component sees the raw wire shape.
//
// App frame: max-width 1440px on browse-app-bg; content padding 26/28/34.
//
// D-3: reminders are device-local — useReminders persists them in
// localStorage and hands the stream to the viewer's own calendar app on
// toggle-on. No backend; onToggleReminder's shape is unchanged.
//
// D-2: after the initial listBroadcasts (which shows skeletons via
// BROADCAST_LIST_REQUEST), useLiveRefresh polls in the background and
// dispatches SUCCESS only, so live status and watcher counts update in
// place — no skeleton flash, no card remount.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import posthog from 'posthog-js';
import { useDispatch, useSelector } from 'react-redux';
import { listBroadcasts } from '../actions/broadcastActions';
import { normalizeStreams } from './stream';
import useLiveRefresh from './useLiveRefresh';
import useReminders from './useReminders';
import BrowseHeader from './BrowseHeader';
import BrowseSections, { BrowseSkeletons } from './BrowseSections';
import FilterSheet from './FilterSheet';
import MobileTabBar, { TAB_BAR_CONTENT_PAD } from './MobileTabBar';

export default function BrowseScreen() {
  const dispatch = useDispatch();
  const { loading, error, broadcasts } = useSelector(
    (state) => state.broadcastList
  );
  // C-2 header state: state tab + search query + park filter + live sort.
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [park, setPark] = useState('all');
  const [sort, setSort] = useState('watched');
  // SOR-625: the mobile Park/Sort bottom sheet
  const [sheetOpen, setSheetOpen] = useState(false);
  const searchTimerRef = useRef(null);

  useEffect(() => {
    dispatch(listBroadcasts());
  }, [dispatch]);
  useLiveRefresh();

  useEffect(() => {
    const q = query.trim();
    if (!q) return;
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      posthog.capture('browse_searched', { query: q });
    }, 500);
    return () => clearTimeout(searchTimerRef.current);
  }, [query]);

  const streams = useMemo(() => normalizeStreams(broadcasts), [broadcasts]);
  const [reminders, toggleReminder] = useReminders(streams);

  // Rising is offered only when the live payload carries the surge fields
  // (server kill switch on) — surging is null on every stream otherwise.
  const hasRisingData = streams.some(
    (st) => st.status === 'live' && st.surging != null
  );
  // Every park/resort tag present in the fetched streams, for the Park pill.
  const parks = useMemo(
    () =>
      [...new Set(streams.flatMap((s) => s.tagNames))].sort((a, b) =>
        a.localeCompare(b)
      ),
    [streams]
  );

  // Tab counts are live values; while a fetch is in flight (the reducer
  // blanks the list) or after an error they are unavailable — pass null so
  // the header OMITS the number rather than showing 0.
  const counts =
    loading || error
      ? {}
      : {
          live: streams.filter((s) => s.status === 'live').length,
          upcoming: streams.filter((s) => s.status === 'upcoming').length,
        };

  // ponytail: search filters the already-fetched list per keystroke —
  // ~dozens of streams, no debounce needed until search hits the network.
  // One predicate serves the grid AND the sheet's "Show N streams" preview
  // (the park is a parameter so the sheet can count against its draft).
  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (s, p) => {
      if (filter === 'live' && s.status !== 'live') return false;
      if (filter === 'upcoming' && s.status !== 'upcoming') return false;
      if (filter === 'saved' && !reminders.has(s.id)) return false;
      if (p !== 'all' && !s.tagNames.includes(p)) return false;
      if (
        q &&
        ![s.title, s.channelName, s.parkCode].some(
          (v) => v && v.toLowerCase().includes(q)
        )
      )
        return false;
      return true;
    };
  }, [filter, reminders, query]);
  const visible = useMemo(
    () => streams.filter((s) => matches(s, park)),
    [streams, matches, park]
  );

  // ponytail: "following" has no backend — reminders are the only
  // follow-shaped signal, so the tab-bar dot lights when a reminded
  // stream's channel is live right now; swap for real follows when they
  // exist.
  const followingLive = useMemo(() => {
    const followed = new Set(
      streams
        .filter((s) => reminders.has(s.id) && s.channelName)
        .map((s) => s.channelName)
    );
    return streams.some(
      (s) => s.status === 'live' && followed.has(s.channelName)
    );
  }, [streams, reminders]);

  const onTabSelect = (tab) => {
    if (tab === 'browse') setFilter('all');
    else if (tab === 'saved') setFilter('saved');
    // ponytail: Following has no destination screen — the tap is captured
    // for posthog in MobileTabBar but does not navigate anywhere yet.
  };

  return (
    <div className="mx-auto max-w-browse-frame bg-browse-app-bg">
      {/* header + grid go inert while the sheet is open (the tab bar gets
          its own inert prop) so the FilterSheet's aria-modal is honest —
          nothing behind the scrim is reachable */}
      <div inert={sheetOpen ? '' : undefined}>
        <BrowseHeader
          query={query}
          onQueryChange={setQuery}
          filter={filter}
          onFilterChange={setFilter}
          counts={counts}
          parks={parks}
          park={park}
          onParkChange={setPark}
          sort={sort}
          onSortChange={setSort}
          showRising={hasRisingData}
          onOpenFilterSheet={() => setSheetOpen(true)}
        />
        {/* SOR-625: mobile bottom padding = tab bar height + safe-area
            inset (TAB_BAR_CONTENT_PAD, owned by MobileTabBar) so the last
            row is never occluded by the fixed bar. px/pt are arbitrary
            spellings — Bootstrap's .px-4/.pt-4 are 24px !important and
            would override both the 16px mobile value and the sm: restore */}
        <div
          id="browse-streams"
          role="tabpanel"
          className={`px-[16px] pt-[16px] ${TAB_BAR_CONTENT_PAD} sm:px-7 sm:pt-6.5 sm:pb-8.5`}
        >
          {error ? (
            <p className="py-8.5 text-center font-inter-tight text-sm text-browse-text-secondary">
              {error}
            </p>
          ) : loading ? (
            <BrowseSkeletons />
          ) : (
            <BrowseSections
              streams={visible}
              reminders={reminders}
              onToggleReminder={toggleReminder}
              sort={sort}
            />
          )}
        </div>
      </div>
      <MobileTabBar
        active={filter === 'saved' ? 'saved' : 'browse'}
        onSelect={onTabSelect}
        followingLive={followingLive}
        inert={sheetOpen}
      />
      <FilterSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        parks={parks}
        park={park}
        onParkChange={setPark}
        sort={sort}
        onSortChange={setSort}
        showRising={hasRisingData}
        countFor={(p) => streams.filter((s) => matches(s, p)).length}
      />
    </div>
  );
}
